import { type QueryKey } from '@tanstack/react-query';
import { memo, type ReactElement, useMemo } from 'react';
import { useAuth } from '@/auth/useAuth';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import {
  useProjectFilter,
  useProjectMetricThresholds,
  useProjectsQuery,
} from '@/features/projects';
import { testsGridConfig } from '@/features/tests/grid/gridConfig';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { type GridFilterModel, type GridSortModel } from '@/ui/primitives';
import { buildTestsColumns } from './testsColumns';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  MetricThresholdGroup,
  QueryResult,
  TestExecutionStatus,
  TestListItemDto,
} from '@flowqa/shared';

type TestsTableProps = {
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  disableColumnSorting?: boolean;
  noRowsText?: string;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<TestListItemDto>>;
  queryKey?: QueryKey;
  onRowsChange?: (rows: TestListItemDto[]) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  showSuiteLinks?: boolean;
  onStatusSelect?: (status: TestExecutionStatus | null) => void;
  showDeltas?: boolean;
  tokenOverride?: string | null;
  disableLinks?: boolean;
};

const TestsTableComponent = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  hideFooter,
  disableColumnSorting,
  noRowsText = 'No tests found',
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  queryFn,
  queryKey,
  onRowsChange,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
  showSuiteLinks = true,
  onStatusSelect,
  showDeltas = true,
  tokenOverride,
  disableLinks = false,
}: TestsTableProps): ReactElement => {
  const { session } = useAuth();
  const resolvedToken = tokenOverride ?? session?.tokens.accessToken ?? null;
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const projectsQuery = useProjectsQuery();
  const thresholdsByProjectId = useMemo(() => {
    const entries = projectsQuery.data?.items ?? [];
    return new Map(entries.map((project) => [project.id, project.metricThresholds.tests]));
  }, [projectsQuery.data?.items]);
  const resolveThresholds = useMemo(
    () =>
      (row: { projectId: string }): MetricThresholdGroup | undefined =>
        thresholdsByProjectId.get(row.projectId),
    [thresholdsByProjectId],
  );
  const defaultColumns = useMemo(
    () =>
      buildTestsColumns({
        selectedProjectId,
        onProjectSelect: setSelectedProjectId,
        onStatusSelect,
        thresholds: thresholds.tests,
        resolveThresholds,
        showSuiteLinks: showSuiteLinks && !disableLinks,
        disableLinks,
        showDeltas,
      }),
    [
      disableLinks,
      onStatusSelect,
      resolveThresholds,
      selectedProjectId,
      setSelectedProjectId,
      showSuiteLinks,
      showDeltas,
      thresholds.tests,
    ],
  );

  const gridConfig = testsGridConfig({
    organisationId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey ?? 'tests-table',
    fixedFilters,
    initialSortModel,
    initialFilterModel,
    includeDeltas: showDeltas,
    include: showSuiteLinks
      ? undefined
      : {
          latestExecution: true,
          metrics: true,
          metricDeltas: showDeltas,
        },
  });

  return (
    <ServerDataTable<TestListItemDto>
      queryPath="tests/query"
      {...gridConfig}
      queryFn={queryFn ?? gridConfig.queryFn}
      queryKey={queryKey ?? gridConfig.queryKey}
      columns={defaultColumns}
      getRowId={(row) => row.id}
      token={resolvedToken}
      organisationId={selectedOrganisationId}
      projectId={selectedProjectId}
      pageSizeOptions={pageSizeOptions}
      initialPageSize={initialPageSize}
      disablePageSizeSelect={disablePageSizeSelect}
      hideFooter={hideFooter}
      disableColumnSorting={disableColumnSorting}
      noRowsText={noRowsText}
      disableCellFocus
      skeletonGetRowHeight={null}
      showLastUpdated={showLastUpdated}
      autoHeight
      onRowsChange={onRowsChange}
      onQueryInput={onQueryInput}
      onDataUpdatedAt={onDataUpdatedAt}
    />
  );
};

export const TestsTable = memo(TestsTableComponent);
